import { useState } from "react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Download, CheckCircle } from "lucide-react";

const PDF_URL = "/QA1st-Ofsted-Readiness-Checklist.pdf";

const LeadMagnetSection = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setLoading(true);
    try {
      const id = crypto.randomUUID();
      const trimmedEmail = email.trim();

      const { error } = await supabase.from("leads").insert({
        id,
        email: trimmedEmail,
        source: "checklist",
      });

      if (error) throw error;

      // Send email notification
      await supabase.functions.invoke("send-transactional-email", {
        body: {
          templateName: "lead-capture-notification",
          recipientEmail: trimmedEmail,
          idempotencyKey: `lead-capture-${id}`,
          templateData: {
            email: trimmedEmail,
            source: "checklist",
          },
        },
      });

      // Trigger download
      const link = document.createElement("a");
      link.href = PDF_URL;
      link.download = "QA1st-Ofsted-Readiness-Checklist.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setSubmitted(true);
      toast.success("Checklist downloading now!");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="bg-accent/5 section-padding border-y border-accent/20">
      <div className="container-narrow text-center max-w-2xl mx-auto">
        <Download className="h-10 w-10 text-accent mx-auto mb-4" />
        <h2 className="mb-4">Download the Ofsted‑Ready Quality System Checklist</h2>
        <p className="text-muted-foreground mb-8">
          A practical framework to assess your quality systems against Ofsted and AAF requirements. Free for apprenticeship providers.
        </p>

        {submitted ? (
          <div className="flex items-center justify-center gap-2 text-accent font-medium">
            <CheckCircle className="h-5 w-5" />
            <span>Your checklist is downloading — check your downloads folder.</span>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Your work email"
              className="flex-1 h-11 rounded-md border border-input bg-card px-4 text-sm"
            />
            <Button variant="cta" type="submit" disabled={loading}>
              {loading ? "Sending…" : "Download Free"}
            </Button>
          </form>
        )}
      </div>
    </section>
  );
};

export default LeadMagnetSection;
